import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { SETUP_STATUS_LABEL } from '@vtf/shared';
import { api } from '../lib/api';
import { hasRole, useMe } from '../lib/session';
import { dirClass, dirLabel, fmtCountdown, fmtTime, useNow } from '../lib/format';
import type { SetupStatus, SetupView } from '../lib/types-setups';
import { Card, cx, Empty, Input, PageHeader, Pill, Segmented } from '../components/ui';

export const SETUP_TONE: Record<SetupStatus, 'neutral' | 'warn' | 'up' | 'accent' | 'down' | 'live'> = {
  WATCHING: 'neutral',
  GETTING_READY: 'warn',
  TRIGGERED: 'up',
  CONVERTED: 'accent',
  INVALIDATED: 'down',
  EXPIRED: 'neutral',
};

export function SetupStatusPill({ status, stale }: { status: SetupStatus; stale?: boolean }) {
  return (
    <span className="inline-flex flex-wrap gap-1.5">
      <Pill tone={SETUP_TONE[status]}>{SETUP_STATUS_LABEL[status]}</Pill>
      {stale && <Pill tone="down">Stale</Pill>}
    </span>
  );
}

export const zoneText = (s: { zoneLow: string | null; zoneHigh: string | null }) => (s.zoneHigh && s.zoneHigh !== s.zoneLow ? `${s.zoneLow} – ${s.zoneHigh}` : (s.zoneLow ?? '—'));

/** "Next update 10:30 (in 42 min)", "Update due now", "Stale since 10:45", or the closed state. */
export function NextUpdate({ s, now, tz, short }: { s: SetupView; now: number; tz: string; short?: boolean }) {
  if (s.stale) return <span className="text-down">Stale since {fmtTime(s.staleSince, tz, true)} · post an update to clear it</span>;
  if (!s.nextUpdate) {
    if (s.closedAt) return <span className="text-muted">Closed {fmtTime(s.closedAt, tz, true)}</span>;
    return <span className="text-muted">No update due before it expires</span>;
  }
  const due = new Date(s.nextUpdate.dueAt).getTime();
  const late = due <= now || s.nextUpdate.status === 'DUE' || s.nextUpdate.status === 'OVERDUE';
  return (
    <span className={cx(late ? 'text-warn' : 'text-ink2')}>
      {late ? 'Update due now' : `Next update ${fmtTime(s.nextUpdate.dueAt, tz, due - now > 20 * 3600_000)}`} <span className="text-muted">({fmtCountdown(s.nextUpdate.dueAt, now)})</span>
      {!short && s.cover && <span className="text-muted"> · {s.cover.name} covering</span>}
    </span>
  );
}

function Fact({ k, v, cls }: { k: string; v: string; cls?: string }) {
  return (
    <span className="rounded-lg bg-raised px-2.5 py-1.5 text-[13px]">
      <span className="text-muted">{k}</span> <span className={cx('font-mono font-semibold', cls)}>{v}</span>
    </span>
  );
}
export { Fact as SetupFact };

export function SetupCard({ s, now, tz }: { s: SetupView; now: number; tz: string }) {
  return (
    <Link to={`/setups/${s.id}`} className="block text-ink no-underline hover:text-ink">
      <Card tone={s.stale ? 'down' : s.status === 'TRIGGERED' ? 'up' : s.status === 'GETTING_READY' ? 'warn' : undefined} className="flex h-full flex-col gap-3 p-4 transition-colors hover:border-line2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-col gap-0.5">
            <span className="text-base font-semibold">
              {s.symbol} <span className={dirClass(s.direction)}>{dirLabel(s.direction)}</span> <span className="font-normal text-muted">· {s.timeframe}</span>
            </span>
            <span className="text-xs text-muted">
              {s.owner.name} · {s.market.code}
              {s.strategy ? ` · ${s.strategy.code}` : ''}
            </span>
          </div>
          <SetupStatusPill status={s.status} stale={s.stale} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Fact k="Zone" v={zoneText(s)} />
          <Fact k="Invalidation" v={s.invalidationPrice ?? '—'} cls="text-down" />
        </div>
        <p className="m-0 line-clamp-2 text-sm leading-relaxed text-ink2">{s.lastUpdate && s.lastUpdate.whatChanged !== 'Turned into a trade plan.' ? s.lastUpdate.whatChanged : s.triggerCondition}</p>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-line pt-2.5 text-xs">
          <NextUpdate s={s} now={now} tz={tz} short />
          <span className="text-muted">
            {s.cadenceLabel}
            {s.followers ? ` · ${s.followers} following` : ''}
          </span>
        </div>
      </Card>
    </Link>
  );
}

const SCOPES = [
  { value: 'floor', label: 'Whole floor' },
  { value: 'mine', label: 'Mine' },
  { value: 'covering', label: 'Covering' },
  { value: 'following', label: 'Following' },
];
const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All' },
];

export function SetupsList() {
  const me = useMe();
  const now = useNow(15_000);
  const tz = me.data?.timezone ?? 'UTC';
  const trader = hasRole(me.data?.roles, 'TRADER', 'MASTER_TRADER');
  const [params] = useSearchParams();
  const [scope, setScope] = useState(() => (SCOPES.some((x) => x.value === params.get('scope')) ? params.get('scope')! : 'floor'));
  const [status, setStatus] = useState('active');
  const [market, setMarket] = useState('');
  const [q, setQ] = useState('');
  const { data: markets } = useQuery({ queryKey: ['markets'], queryFn: () => api<{ id: string; code: string }[]>('/markets'), staleTime: 300_000 });
  const query = new URLSearchParams({ scope, status, ...(market ? { marketId: market } : {}), ...(q.trim() ? { q: q.trim() } : {}) });
  const { data, isLoading } = useQuery({ queryKey: ['setups', scope, status, market, q.trim()], queryFn: () => api<SetupView[]>(`/setups?${query}`), refetchInterval: 60_000 });

  return (
    <>
      <PageHeader
        title="Setups"
        sub="What the floor is watching · updates appear live"
        right={
          trader && (
            <Link to="/setups/new" className="rounded-lg bg-accent px-4 py-2.5 font-bold text-bg no-underline hover:text-bg">
              + Mark a setup
            </Link>
          )
        }
      />
      <div className="flex flex-wrap items-center gap-3">
        <Segmented label="Whose setups" options={trader ? SCOPES : SCOPES.filter((x) => x.value === 'floor' || x.value === 'following')} value={scope} onChange={setScope} />
        <Segmented label="Status" options={STATUSES} value={status} onChange={setStatus} />
        <select aria-label="Market" value={market} onChange={(e) => setMarket(e.target.value)} className="min-h-10 rounded-lg border border-line2 bg-field px-3 text-ink">
          <option value="">All markets</option>
          {markets?.map((m) => (
            <option key={m.id} value={m.id}>
              {m.code}
            </option>
          ))}
        </select>
        <Input aria-label="Search setups" placeholder="Search symbol, trader or trigger" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-[300px] min-w-[200px] flex-1" />
      </div>
      <section aria-label="Setups" className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
        {isLoading && <Card className="p-4 text-sub">Loading…</Card>}
        {!isLoading && !data?.length && (
          <Card className="sm:col-span-2 xl:col-span-3">
            <Empty>
              {status === 'active' ? (scope === 'mine' ? 'You have no active setups. Mark one when you start watching a level.' : 'No active setups here.') : 'No setups match.'}
            </Empty>
          </Card>
        )}
        {data?.map((s) => <SetupCard key={s.id} s={s} now={now} tz={tz} />)}
      </section>
    </>
  );
}
